import http from './http';

/**
 * Consultar o cadastro do cliente por registro ou CNPJ, SEM associar nada.
 *
 * Ponto 15 da Gisele (20/10/2026): "ao associar o CNPJ ou registro do cliente
 * no WhatsApp, mostrar os dados encontrados na própria janela de edição".
 *
 * `GET /clients/consultar-cadastro` é de leitura: não cria empresa nem
 * vínculo. É o que permite conferir um registro digitado errado sem deixar
 * cadastro novo na base -- e é justamente para não errar que o operador quer
 * ver os dados antes.
 */

/**
 * O que o servidor espera receber.
 *
 * A pontuação do CNPJ sai aqui porque é assim que as pessoas colam:
 * `49.752.276/0002-04`. Quem reconhece se é CNPJ ou registro é a fonte do
 * tenant, no servidor -- este módulo só tira o que atrapalha a comparação.
 *
 * O registro é numérico e não perde nada na limpeza. Um identificador livre
 * com letras (a base importada aceita) também passa intacto: só pontuação sai.
 */
export const limparIdentificacao = (bruto) => String(bruto || '')
    .replace(/[.\-/\s]/g, '')
    .trim();

/** Os três desfechos possíveis, para a tela não depender de `catch` genérico. */
export const ENCONTRADO = 'encontrado';
export const NAO_ENCONTRADO = 'nao-encontrado';
export const FALHOU = 'falhou';

/**
 * @param {string} identificacao registro ou CNPJ, como foi digitado
 * @returns {Promise<{situacao: string, cadastro?: object, mensagem?: string}>}
 */
export const consultarCadastro = async (identificacao) => {
    const limpa = limparIdentificacao(identificacao);

    if (limpa === '') {
        return { situacao: NAO_ENCONTRADO, mensagem: 'Informe o registro ou o CNPJ do cliente.' };
    }

    try {
        const res = await http.get('/clients/consultar-cadastro', {
            params: { identificacao: limpa },
        });

        return { situacao: ENCONTRADO, cadastro: res.data };
    } catch (erro) {
        /*
         * 404 e falha de rede são desfechos DIFERENTES, e a diferença muda o
         * conselho: "não encontramos esse registro" manda conferir o número,
         * "não foi possível consultar" manda tentar de novo. Colapsar os dois
         * num `catch` só faria a tela culpar quem digitou por uma queda do
         * ERP.
         */
        const status = erro?.response?.status;

        if (status === 404 || status === 422) {
            return {
                situacao: NAO_ENCONTRADO,
                mensagem: erro?.response?.data?.message
                    || `Nenhum cadastro encontrado para ${limpa}.`,
            };
        }

        return {
            situacao: FALHOU,
            mensagem: 'Não foi possível consultar o cadastro agora. Tente novamente.',
        };
    }
};

export default consultarCadastro;
